import React, { useEffect, useRef } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
	Hidden,
	Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { RiArrowDownSLine } from 'react-icons/ri';
import { Translatable } from 'common/types';
import TranslatableField from './TranslatableField';
import ProgressCircle from './ProgressCircle';
import { getTranslationPercentage } from 'common/modules/translations';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import { useTranslation } from 'services/localization';
import { getLanguageName } from 'common/locales/utils';
import { TranslationGroup } from 'reducers/TranslationsReducer';

interface Props {
	data: Translatable;
	expanded: boolean;
	onExpandedChange: (id: string, expanded: boolean) => void;
	group: TranslationGroup;
}

const TranslatableItem = React.memo((props: Props) => {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const activeLanguage = useSelector(TranslationsSelectors.activeLanguage);
	const defaultLanguage = useSelector(TranslationsSelectors.activeDefaultLanguage);

	const { expanded, onExpandedChange, group, data } = props;
	const { label, fields, id } = props.data;

	useEffect(() => {
		if (expanded) {
			ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [label, expanded]);

	return (
		<Box sx={{ position: 'relative' }}>
			<Box ref={ref} sx={{ position: 'relative', top: -90 }} />
			<StyledAccordion
				elevation={0}
				square
				expanded={expanded}
				onChange={(_, expanded) => onExpandedChange(id, expanded)}
				TransitionProps={{ unmountOnExit: true }}
				disableGutters
			>
				<StyledAccordionSummary expandIcon={<RiArrowDownSLine />}>
					<AccordionSummaryContent>
						<ProgressCircle value={getTranslationPercentage(data, activeLanguage).percentage} />
						<Box mr={2} />
						<UppercaseText
							variant="body1"
							sx={{
								fontWeight: 500,
							}}
						>
							{label}
						</UppercaseText>
					</AccordionSummaryContent>
				</StyledAccordionSummary>
				<StyledAccordionDetails>
					<Box mt={2} />
					<Hidden smDown>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<UppercaseText>
									{getLanguageName(defaultLanguage, t)} - {t('account.languages.main')}
								</UppercaseText>
							</Grid>
							<Grid item xs={6}>
								<UppercaseText>
									{getLanguageName(activeLanguage, t)} -{' '}
									{t('account.languages.translation', 'Translation')}
								</UppercaseText>
							</Grid>
						</Grid>
					</Hidden>
					{Object.values(fields).map((field) => (
						<TranslatableField key={field.id} field={field} docId={data.id} group={group} />
					))}
				</StyledAccordionDetails>
			</StyledAccordion>
		</Box>
	);
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
	borderTop: `1px solid ${theme.palette.divider}`,
	'&:before': {
		display: 'none',
	},
	'&:first-of-type': {
		borderTop: 'none',
	},
	position: 'relative',
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
	padding: theme.spacing(1, 2),
	position: 'sticky',
	top: 90,
	background: theme.palette.background.paper,
	borderBottom: `1px solid ${theme.palette.divider}`,
	zIndex: 2,
	'&:focus': {
		background: theme.palette.background.secondary,
	},
	'&:hover': {
		background: theme.palette.background.secondary,
	},
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
	paddingLeft: theme.spacing(3),
	paddingRight: theme.spacing(3),
	background: theme.palette.background.paper,
}));

const AccordionSummaryContent = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	width: '100%',
	marginRight: theme.spacing(2),
}));

const UppercaseText = styled(Typography)(({ theme }) => ({
	textTransform: 'uppercase',
	fontSize: '14px',
	color: theme.palette.text.secondary,
}));

export default TranslatableItem;
